import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "../../components/ui/table";
import { Building2, Search, Plus, Pencil, Trash2, Loader2, Truck, MapPin } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { formatDate } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const initialCompany = {
  name: "",
  description: "",
  address: "",
  city: "",
  province: "",
  cap: "",
  phone: "",
  email: "",
  vat_number: ""
};

export default function AdminCompanies() {
  const { token } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState(initialCompany);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [companiesRes, vehiclesRes] = await Promise.all([
        axios.get(`${API}/companies`, { headers, withCredentials: true }),
        axios.get(`${API}/vehicles`, { headers, withCredentials: true })
      ]);
      
      setCompanies(companiesRes.data);
      
      // Group vehicles by company
      const vehiclesByCompany = {};
      vehiclesRes.data.forEach((v) => {
        if (!vehiclesByCompany[v.company_id]) {
          vehiclesByCompany[v.company_id] = [];
        }
        vehiclesByCompany[v.company_id].push(v);
      });
      setVehicles(vehiclesByCompany);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const filteredCompanies = companies.filter((company) => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (!company.name.toLowerCase().includes(search) && 
          !company.city.toLowerCase().includes(search) &&
          !company.vat_number.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  const openAddDialog = () => {
    setEditingCompany(null);
    setFormData(initialCompany);
    setShowDialog(true);
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name || "",
      description: company.description || "",
      address: company.address || "",
      city: company.city || "",
      province: company.province || "",
      cap: company.cap || "",
      phone: company.phone || "",
      email: company.email || "",
      vat_number: company.vat_number || ""
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      if (editingCompany) {
        await axios.put(`${API}/companies/${editingCompany.company_id}`, formData, { headers, withCredentials: true });
        toast.success("Azienda aggiornata");
      } else {
        await axios.post(`${API}/companies`, formData, { headers, withCredentials: true });
        toast.success("Azienda creata");
      }

      setShowDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCompany = async (companyId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questa azienda? Tutti i veicoli associati saranno eliminati.")) return;

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/companies/${companyId}`, { headers, withCredentials: true });
      toast.success("Azienda eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  return (
    <DashboardLayout title="Gestione Aziende">
      <div className="space-y-6" data-testid="admin-companies">
        {/* Header */}
        <div className="flex flex-col md:flex-row gap-4 justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per nome, città o P.IVA..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-companies"
            />
          </div>
          <Button onClick={openAddDialog} className="btn-primary py-2" data-testid="add-company-btn">
            <Plus className="h-4 w-4 mr-2" />
            Aggiungi Azienda
          </Button>
        </div>

        {/* Companies Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="py-12 text-center">
                <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Nessuna azienda trovata</p>
                <Button onClick={openAddDialog} className="mt-4">
                  Aggiungi la prima azienda
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Azienda</TableHead>
                    <TableHead>P.IVA</TableHead>
                    <TableHead>Località</TableHead>
                    <TableHead>Contatti</TableHead>
                    <TableHead>Veicoli</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.map((company) => (
                    <TableRow key={company.company_id} data-testid={`company-row-${company.company_id}`}>
                      <TableCell>
                        <div className="font-medium">{company.name}</div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {company.vat_number}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {company.city}, {company.province}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{company.phone}</div>
                          <div className="text-muted-foreground">{company.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Truck className="h-4 w-4 text-muted-foreground" />
                          <span className="font-mono">{vehicles[company.company_id]?.length || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={company.is_active !== false ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                          {company.is_active !== false ? "Attiva" : "Disattiva"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(company)}
                            data-testid={`edit-company-${company.company_id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={() => deleteCompany(company.company_id)}
                            data-testid={`delete-company-${company.company_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                {editingCompany ? "Modifica Azienda" : "Nuova Azienda"}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Nome Azienda *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="es. Noleggio Rossi Srl"
                    required
                    data-testid="company-name"
                  />
                </div>
                <div>
                  <Label className="input-label">Partita IVA *</Label>
                  <Input
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    placeholder="IT12345678901"
                    required
                    data-testid="company-vat"
                  />
                </div>
              </div>

              <div>
                <Label className="input-label">Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrizione dell'azienda..."
                  rows={2}
                />
              </div>

              <div>
                <Label className="input-label">Indirizzo *</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Via Roma 1"
                  required
                  data-testid="company-address"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="input-label">Città *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Milano"
                    required
                    data-testid="company-city"
                  />
                </div>
                <div>
                  <Label className="input-label">Provincia *</Label>
                  <Input
                    value={formData.province}
                    onChange={(e) => setFormData({ ...formData, province: e.target.value.toUpperCase() })}
                    placeholder="MI"
                    maxLength={2}
                    required
                    data-testid="company-province"
                  />
                </div>
                <div>
                  <Label className="input-label">CAP *</Label>
                  <Input
                    value={formData.cap}
                    onChange={(e) => setFormData({ ...formData, cap: e.target.value })}
                    placeholder="20100"
                    required
                    data-testid="company-cap"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="input-label">Telefono *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+39 02 1234567"
                    required
                    data-testid="company-phone"
                  />
                </div>
                <div>
                  <Label className="input-label">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="info@azienda.it"
                    required
                    data-testid="company-email"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Annulla
                </Button>
                <Button type="submit" className="btn-primary py-2" disabled={submitting} data-testid="save-company">
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    editingCompany ? "Salva Modifiche" : "Crea Azienda"
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
